<script setup>
import { ref, computed, nextTick, onMounted } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';

const store = useStore();
const router = useRouter();
const { t } = useI18n();
const { accountId } = useAccount();

const boards = computed(() => store.getters['tasks/getBoards']);

const isCreating = ref(false);
const newName = ref('');
const isSaving = ref(false);
const nameInput = ref(null);

const openMenuId = ref(null);
const renamingId = ref(null);
const renameValue = ref('');
const renameInput = ref(null);

const menuItems = [
  {
    label: t('TASKS.RENAME'),
    action: 'rename',
    value: 'rename',
    icon: 'i-lucide-pencil',
  },
  {
    label: t('TASKS.DELETE_BOARD'),
    action: 'delete',
    value: 'delete',
    icon: 'i-lucide-trash-2',
  },
];

onMounted(() => {
  store.dispatch('tasks/fetchBoards');
});

const columnDots = board =>
  [...(board.columns || [])]
    .sort((a, b) => a.position - b.position)
    .slice(0, 6)
    .map(column => column.color || '#64748b');

const openBoard = boardId => {
  router.push({
    name: 'tasks_board_view',
    params: { accountId: accountId.value, boardId },
  });
};

const startCreating = async () => {
  isCreating.value = true;
  newName.value = '';
  await nextTick();
  nameInput.value?.focus();
};

const cancelCreating = () => {
  isCreating.value = false;
  newName.value = '';
};

const createBoard = async () => {
  const name = newName.value.trim();
  if (!name || isSaving.value) return;
  isSaving.value = true;
  try {
    const board = await store.dispatch('tasks/createBoard', { name });
    cancelCreating();
    openBoard(board.id);
  } finally {
    isSaving.value = false;
  }
};

const startRenaming = async board => {
  renamingId.value = board.id;
  renameValue.value = board.name;
  await nextTick();
  const el = renameInput.value;
  (Array.isArray(el) ? el[0] : el)?.focus();
};

const saveRename = async board => {
  const name = renameValue.value.trim();
  renamingId.value = null;
  if (!name || name === board.name) return;
  await store.dispatch('tasks/updateBoard', { id: board.id, name });
};

const deleteBoard = async board => {
  await store.dispatch('tasks/deleteBoard', board.id);
  useAlert(t('TASKS.BOARD_DELETED', { name: board.name }));
};

const onMenuAction = (board, { action }) => {
  openMenuId.value = null;
  if (action === 'rename') startRenaming(board);
  else if (action === 'delete') deleteBoard(board);
};
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-auto bg-n-background">
    <div class="flex items-center justify-between px-8 py-6">
      <h1 class="text-xl font-medium text-n-slate-12">
        {{ t('TASKS.HEADER') }}
      </h1>
      <button
        v-if="!isCreating"
        class="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg bg-n-brand hover:bg-n-brand/90"
        @click="startCreating"
      >
        <span class="i-lucide-plus size-4" />
        {{ t('TASKS.NEW_BOARD') }}
      </button>
    </div>

    <div class="px-8 pb-8">
      <form
        v-if="isCreating"
        class="flex items-center gap-2 p-4 mb-6 border rounded-xl bg-n-solid-2 border-n-weak"
        @submit.prevent="createBoard"
      >
        <input
          ref="nameInput"
          v-model="newName"
          type="text"
          :placeholder="t('TASKS.BOARD_NAME')"
          class="flex-1 px-3 py-2 text-sm border rounded-lg outline-none bg-n-alpha-black2 text-n-slate-12 border-n-weak focus:border-n-brand"
        />
        <button
          type="submit"
          :disabled="isSaving || !newName.trim()"
          class="px-4 py-2 text-sm font-medium text-white rounded-lg bg-n-brand hover:bg-n-brand/90 disabled:opacity-50"
        >
          {{ t('TASKS.CREATE') }}
        </button>
        <button
          type="button"
          class="px-3 py-2 text-sm rounded-lg text-n-slate-11 hover:bg-n-alpha-2"
          @click="cancelCreating"
        >
          {{ t('TASKS.CANCEL') }}
        </button>
      </form>

      <div
        v-if="boards.length"
        class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
      >
        <div
          v-for="board in boards"
          :key="board.id"
          class="relative flex flex-col gap-3 p-5 transition-colors border cursor-pointer group rounded-xl bg-n-solid-2 border-n-weak hover:border-n-brand"
          @click="renamingId === board.id ? null : openBoard(board.id)"
        >
          <div class="flex items-start justify-between">
            <span class="i-lucide-square-kanban size-6 text-n-brand" />
            <div
              v-on-clickaway="() => (openMenuId = null)"
              class="relative"
              @click.stop
            >
              <button
                class="p-1 rounded-md opacity-0 text-n-slate-11 group-hover:opacity-100 hover:bg-n-alpha-2"
                :class="{ 'opacity-100': openMenuId === board.id }"
                @click="openMenuId = openMenuId === board.id ? null : board.id"
              >
                <span class="i-lucide-ellipsis size-4" />
              </button>
              <DropdownMenu
                v-if="openMenuId === board.id"
                :menu-items="menuItems"
                class="right-0 mt-1 top-full"
                @action="onMenuAction(board, $event)"
              />
            </div>
          </div>

          <input
            v-if="renamingId === board.id"
            ref="renameInput"
            v-model="renameValue"
            type="text"
            class="w-full px-2 py-1 text-base font-medium border rounded-md outline-none bg-n-alpha-black2 text-n-slate-12 border-n-brand"
            @click.stop
            @keydown.enter.prevent="saveRename(board)"
            @keydown.esc="renamingId = null"
            @blur="saveRename(board)"
          />
          <span
            v-else
            class="text-base font-medium text-n-slate-12 line-clamp-1"
          >
            {{ board.name }}
          </span>

          <div class="flex items-center gap-1">
            <span
              v-for="(dot, index) in columnDots(board)"
              :key="index"
              class="h-1.5 w-6 rounded-full"
              :style="{ backgroundColor: dot }"
            />
          </div>

          <span class="text-xs text-n-slate-11">
            {{ t('TASKS.CARD_COUNT', { count: board.tasks_count || 0 }) }}
          </span>
        </div>
      </div>

      <div
        v-else-if="!isCreating"
        class="flex flex-col items-center justify-center gap-4 py-24 text-center"
      >
        <span class="text-n-slate-9 i-lucide-square-kanban size-12" />
        <p class="text-sm text-n-slate-11">{{ t('TASKS.EMPTY') }}</p>
        <button
          class="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg bg-n-brand hover:bg-n-brand/90"
          @click="startCreating"
        >
          <span class="i-lucide-plus size-4" />
          {{ t('TASKS.NEW_BOARD') }}
        </button>
      </div>
    </div>
  </div>
</template>
